import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import { getConnectionsList } from '../../api/connections/connectionsApi';
import { Connection, Organization, ProviderType, DestinationType } from '../common/ObjectTypes';
import ConnectionsList from './ConnectionsList';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from '../../api/apiUtils';
import { useKeycloakToken } from '../common/useKeycloak';
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import { loadProviderTypes } from '../../api/connections/loadProviderTypesFilter';
import getUserPermissions from '../../api/users/getUserPermissions';
import { useKeycloak } from '@react-keycloak/web';
import { getEnvironment } from '../common/getEnvironment';
import { useHelp } from '../../context/HelpContext';
import { useTranslation } from 'react-i18next';

const ConnectionsPage = () => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        setHelpContent({
            cards: [{
                title: t('Managing Connections in Gundi'),
                description: t('Learn how to create and manage connections between data sources and destinations.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/' },
            }],
        });
    }, [i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    type FilterTypes = {
        search: string,
        provider_type__in: FilterTags[],
        destination_type__in: FilterTags[],
        destination_url__in: FilterTags[],
        owner__in: FilterTags[],
        status: FilterTags[],
        ordering: string,
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultConnectionsFilter = {
        provider_type__in: [],
        destination_type__in: [],
        destination_url__in: [],
        owner__in: [],
        status: [],
        search: "",
        ordering: "name"
    };
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const currentEnv = getEnvironment();
    const [canAddConnection, setCanAddConnection] = useState(false);
    const [connections, setConnections] = useState<Connection[]>([]);
    const [connectionsFilter, setConnectionsFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('connectionsFilter');
        return savedFilter ? { ...defaultConnectionsFilter, ...JSON.parse(savedFilter) } : defaultConnectionsFilter;
    });
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [loadingProviderTypes, setLoadingProviderTypes] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [reloadConnections, setReloadConnections] = useState(true);
    const navigate = useNavigate();

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                let accumulated: Organization[] = [];
                let orgsOffset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(token, orgsOffset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (orgsOffset + filter_page_size >= total) break;
                    orgsOffset += filter_page_size;
                }
                setOrganizations(accumulated);
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let urlsOffset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, urlsOffset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (urlsOffset + filter_page_size >= total) break;
                    urlsOffset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadProviderTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingProviderTypes(true);
            try {
                let accumulated: ProviderType[] = [];
                let offset = 0;
                while (true) {
                    const [types, total, error] = await loadProviderTypes(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setProviderTypes(accumulated);
            } finally {
                setLoadingProviderTypes(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let offset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    useEffect(() => {
        sessionStorage.setItem('connectionsFilter', JSON.stringify(connectionsFilter));
        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddConnection(temp);
                } catch (err) {
                    console.log(err)
                }
            }
        }

        const loadConnections = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const connectionFilterQuery = {
                        provider_type__in: connectionsFilter.provider_type__in.map(a => a.id).toString(),
                        destination_type__in: connectionsFilter.destination_type__in.map(a => a.id).toString(),
                        destination_url__in: connectionsFilter.destination_url__in.map(a => (a.id)).toString(),
                        owner__in: connectionsFilter.owner__in.map(a => a.id).toString(),
                        status: connectionsFilter.status.map(a => a.id).toString(),
                        ordering: connectionsFilter.ordering,
                        search: connectionsFilter.search,
                        offset: offset,
                        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/connections/?pager=limit&limit=" + page_size + "&offset=" + (offset - page_size),
                        next: process.env.REACT_APP_BASE_URL_LOCAL + "/connections/?pager=limit&limit=" + page_size + "&offset=" + (offset + page_size)
                    }
                    if (connectionsFilter.search !== '') {
                        ReactGA.event('connections_search', {
                            search_term: connectionsFilter.search || 'none',
                            environment: currentEnv
                        });
                    }
                    const temp = await getConnectionsList(token, connectionFilterQuery)
                    setConnections(temp.results)
                    setTotalCount(temp.count);
                    setReloadConnections(false);
                    setPageLoaded(true);
                } catch (err) {
                    console.log(err)
                }
            }
        }

        if (reloadConnections) loadConnections();
        if (!pageLoaded) {
            loadPerms();
            loadOrganizationsData();
            loadDestinationURlsData();
            loadProviderTypesData();
            loadDestinationTypesData();
        }
    }, [token, reloadConnections, connectionsFilter]);

    const handleGlobalSearch = (searchSubStr: string) => {
        setConnectionsFilter((prevFilter: any) => ({
            ...prevFilter,
            search: searchSubStr
        }));
        setOffset(0);
        setReloadConnections(true);
    }

    const handleFilterByStatus = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setConnectionsFilter(prev => ({ ...prev, status: tags }));
        setOffset(0);
        setReloadConnections(true);
    }

    const handleFilterDestinationUrl = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setConnectionsFilter(prev => ({ ...prev, destination_url__in: tags }));
        setOffset(0);
        setReloadConnections(true);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setConnectionsFilter(prev => ({ ...prev, owner__in: tags }));
        setOffset(0);
        setReloadConnections(true);
    }

    const handleFilterProviderType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setConnectionsFilter(prev => ({ ...prev, provider_type__in: tags }));
        setOffset(0);
        setReloadConnections(true);
    }

    const handleFilterDestinationType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setConnectionsFilter(prev => ({ ...prev, destination_type__in: tags }));
        setOffset(0);
        setReloadConnections(true);
    }

    const handleSortTable = (orderBy: string, sortDirection: boolean) => {
        const ordering = sortDirection ? orderBy : "-" + orderBy;
        setConnectionsFilter((prevFilter: any) => ({
            ...prevFilter,
            ordering: ordering
        }))
        setReloadConnections(true);
    }

    const handleSelectConnection = (connection_id: string) => {
        navigate("/connections/" + connection_id)
    }

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setReloadConnections(true);
    }

    const handleClickAdd = () => {
        ReactGA.event({
            category: 'User Interaction',
            action: 'Clicked Add Connection Button',
            label: 'Create Connection'
        });
        navigate("/connections/new")
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const statusSelectValue = toSingleValue(connectionsFilter.status);
    const destinationSelectValue = toSingleValue(connectionsFilter.destination_url__in);
    const ownerSelectValue = toSingleValue(connectionsFilter.owner__in);
    const providerTypeSelectValue = toSingleValue(connectionsFilter.provider_type__in);
    const destinationTypeSelectValue = toSingleValue(connectionsFilter.destination_type__in);

    return (
            <div>
                <ConnectionsList
                    canAddConnection={canAddConnection}
                    connections={connections}
                    reloadConnections={reloadConnections}
                    organizations={organizations}
                    destinationUrls={destinationUrls}
                    providerTypes={providerTypes}
                    loadingProviderTypes={loadingProviderTypes}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    onSelectConnection={handleSelectConnection}
                    onChangeGlobalSearch={handleGlobalSearch}
                    onChangeStatusFilter={handleFilterByStatus}
                    statusFilterValue={statusSelectValue}
                    onChangeDestinationUrl={handleFilterDestinationUrl}
                    destinationFilterValue={destinationSelectValue}
                    loadingDestinationUrls={loadingDestinationUrls}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    loadingOrganizations={loadingOrganizations}
                    onChangeProviderType={handleFilterProviderType}
                    providerTypeFilterValue={providerTypeSelectValue}
                    onChangeDestinationType={handleFilterDestinationType}
                    destinationTypeFilterValue={destinationTypeSelectValue}
                    globalSearchStr={connectionsFilter.search}
                    requestSort={handleSortTable}
                    onPageClicked={handlePageClick}
                    totalCount={totalCount}
                    pageLoaded={pageLoaded}
                    take={page_size}
                    offset={offset}
                    onClickAdd={handleClickAdd}
                />
            </div>
        );
};

export default ConnectionsPage;
